import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { PageViewer } from '../../components/PageViewer';
import {
  PATH_RESOLVABLE_SERVICE_GENERAL,
  PATH_RESOLVABLE_SERVICE_GENERAL_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableServiceForm,
  ResolvableServiceFormTab,
} from '../../forms/ResolvableService';
import { Code } from '../../components/Code';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const ResolvableServicePage_GeneralInfo_ResolvableServiceBaseInterface_Fragment =
  graphql(`
    fragment ResolvableServicePage_GeneralInfo_ResolvableServiceBaseInterface_Fragment on ResolvableServiceBaseInterface {
      _id
      codingConfig {
        kind
        strategyId
        ... on CodingConfigManual {
          code
        }
        ...CodeCmp_CodingConfig_Fragment
      }
      code
      label
      title
      content
      ...ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment
    }
  `);

interface Props {
  readonly resolvableService: FragmentType<
    typeof ResolvableServicePage_GeneralInfo_ResolvableServiceBaseInterface_Fragment
  >;
}

export const GeneralInfo = ({
  resolvableService: pResolvableService,
}: Readonly<Props>) => {
  const resolvableService = getFragmentData(
    ResolvableServicePage_GeneralInfo_ResolvableServiceBaseInterface_Fragment,
    pResolvableService,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_SERVICE_GENERAL,
      edit: PATH_RESOLVABLE_SERVICE_GENERAL_EDIT,
    },
    { id: resolvableService._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Service
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code
              code={resolvableService.code}
              codingConfig={resolvableService.codingConfig}
            />
            <Typography gutterBottom variant="body1">
              Libellé : {resolvableService.label}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Document
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
            {resolvableService.title || (
              <Typography gutterBottom variant="body1">
                Titre non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {resolvableService.content ? (
              <PageViewer html={resolvableService.content} />
            ) : (
              <Typography gutterBottom variant="body1">
                Contenu non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editServiceGeneralInfo',
            icon: <EditIcon />,
            name: 'Modifier Service',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Générales du Service"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableServiceForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableService}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableServiceId={resolvableService._id}
          stepIdsSubset={[ResolvableServiceFormTab.ResolvableServiceInfo]}
          title="Formulaire de modification des Informations Générales du Service"
        />
      </RightDrawer>
    </>
  );
};
